import {
  collection,
  getDocs,
  limit as fsLimit,
  orderBy,
  query,
  startAfter,
  where,
  type QueryConstraint,
  type QueryDocumentSnapshot,
} from 'firebase/firestore';
import {
  AuditBillingEventSchema,
  type AuditBillingEvent,
  type AuditBillingFilters,
} from '@etiquetachef/types';
import { firestore } from './firebase';
import { reportNonFatal, serializeError } from './errorReport';

const TAG = 'admin/auditBilling';

export const AUDIT_BILLING_PAGE_SIZE = 50;

export type AuditBillingEventRow = AuditBillingEvent;

export type AuditBillingPage = {
  rows: AuditBillingEventRow[];
  /** Último doc da página — passar pra `loadMoreAuditBillingEvents` pra paginar. */
  cursor: QueryDocumentSnapshot | null;
  hasMore: boolean;
};

/**
 * Lê uma página de eventos do log financeiro `audit/billing/items`.
 *
 * Origem: sem issue — commit 6b847501f, 2026-05-26 00:56 +0200 (Sprint 35
 * PR H1 — tela /audit/billing + filtros + export CSV).
 *
 * Por quê: cursor-based (`startAfter(lastDoc)`) ao invés de realtime —
 * audit é write-once, a lista cresce devagar, e listener em coleção grande
 * seria caro. O cliente clica "Carregar mais" para trazer a próxima página.
 *
 * Contrato: recebe `AuditBillingFilters` (`from`/`to` em ms epoch,
 * inclusivos; `tenantId`/`action`/`actorType`/`externalEventId` ou `null`)
 * e o cursor da página anterior (`null` na primeira). Devolve até
 * `AUDIT_BILLING_PAGE_SIZE` (50) linhas, o último doc como cursor e
 * `hasMore` (página cheia → talvez haja mais; pode dar um clique vazio no
 * fim). Com `externalEventId` faz lookup direto (sem range de data, sem
 * paginação — `hasMore: false`). Erro do Firestore (`permission-denied`,
 * `failed-precondition` por índice faltando) é relançado ao chamador. Só
 * leitura; nenhuma escrita.
 *
 * Usado por: `routes/AuditBillingPage.tsx` (rota `/audit/billing`, menu
 * Sistema → "Auditoria · Billing"), carga inicial e "Carregar mais". Só
 * `apps/admin` (web); o export CSV vai pela callable `exportAuditBilling`,
 * que replica as mesmas combinações de filtro no servidor.
 *
 * Arquitetura: leitura direta pelo client SDK, autorizada pela regra
 * `match /audit/billing/items/{eventId}` (`firebase/firestore.rules`, Lê:
 * super-admin; Escreve: só servidor via `logAuditBilling`). Combinações de
 * filtro suportadas (índices `items` COLLECTION no
 * `firebase/firestore.indexes.json`, issue #8):
 *   - apenas `at` range (default; índice single-field automático)
 *   - `tenantId == X` + `at` range
 *   - `action == X` + `at` range
 *   - `actorType == X` + `at` range
 *   - quaisquer pares (`tenantId`+`action`, `tenantId`+`actorType`,
 *     `action`+`actorType`) + `at` range
 *   - o trio (`tenantId`+`action`+`actorType`) + `at` range
 *   - `externalEventId == X` (sem orderBy at — lookup direto)
 * O UI (`AuditBillingPage`) deixa os três filtros editáveis ao mesmo
 * tempo, então TODAS as combinações acima têm índice (`<campos>` ASC +
 * `at` DESC).
 *
 * Dados: `audit/billing/items/{eventId}` (`AuditBillingEventSchema`,
 * `packages/types/src/schemas.ts`). Exemplo mínimo anonimizado:
 *   {
 *     "at": 1780000000000,                 // ms epoch, servidor
 *     "actorType": "SUPER_ADMIN", "actorId": "uid-1",
 *     "actorIpHash": null,                 // zerado após 90 d
 *     "actorUserAgent": null,
 *     "action": "<AuditBillingAction>", "tenantId": "tenant-1",
 *     "subscriptionId": null,
 *     "amountCents": 12990,                // CENTAVOS (ou null)
 *     "currency": "BRL",                   // ou null
 *     "externalProvider": null, "externalEventId": null,
 *     "reason": null, "before": {}, "after": {}, "metadata": {}
 *   }
 * O `eventId` vem do id do doc (mesclado antes do parse).
 *
 * Falhas conhecidas:
 *  - issue #8: a query combinada (dois ou três filtros) batia
 *    `failed-precondition` em runtime (página/export quebravam) por falta
 *    dos índices compostos.
 *
 * Dicas: ao adicionar filtro novo, adicionar o índice da combinação no
 * mesmo commit (e na callable `exportAuditBilling`). Doc que falha no
 * schema é descartado e reportado (`admin_audit_billing_parse_failed`, ver
 * `parseRows`); até 2026-10-13 era só `console.warn` e a linha sumia em
 * silêncio (#935, item 14).
 *
 * Issues: #8 (índices das combinações).
 *
 * Histórico:
 *  - 2026-10-10 14:53 +0200 — (#798) cabeçalho padrão (#796); caminho
 *    corrigido de `/audit/billing/{eventId}` para `.../items/{eventId}`.
 */
export async function fetchAuditBillingPage(
  filters: AuditBillingFilters,
  cursor: QueryDocumentSnapshot | null,
): Promise<AuditBillingPage> {
  const constraints: QueryConstraint[] = [];
  const ref = collection(firestore, 'audit', 'billing', 'items');

  // Lookup direto por externalEventId — sem orderBy/range, é único.
  if (filters.externalEventId !== null) {
    constraints.push(where('externalEventId', '==', filters.externalEventId));
    constraints.push(fsLimit(AUDIT_BILLING_PAGE_SIZE));
    const snap = await getDocs(query(ref, ...constraints));
    const rows = parseRows(snap.docs);
    return { rows, cursor: null, hasMore: false };
  }

  if (filters.tenantId !== null) {
    constraints.push(where('tenantId', '==', filters.tenantId));
  }
  if (filters.action !== null) {
    constraints.push(where('action', '==', filters.action));
  }
  if (filters.actorType !== null) {
    constraints.push(where('actorType', '==', filters.actorType));
  }
  constraints.push(where('at', '>=', filters.from));
  constraints.push(where('at', '<=', filters.to));
  constraints.push(orderBy('at', 'desc'));
  if (cursor !== null) {
    constraints.push(startAfter(cursor));
  }
  constraints.push(fsLimit(AUDIT_BILLING_PAGE_SIZE));

  try {
    const snap = await getDocs(query(ref, ...constraints));
    const rows = parseRows(snap.docs);
    const nextCursor =
      snap.docs.length > 0 ? (snap.docs[snap.docs.length - 1] ?? null) : null;
    return {
      rows,
      cursor: nextCursor,
      hasMore: snap.docs.length >= AUDIT_BILLING_PAGE_SIZE,
    };
  } catch (err) {
    // #935 (item 14, 2026-10-13 11:00 +0200): só `console.*` não chega até nós — o admin
    // não manda DevTools. O report leva a falha ao PostHog (CLAUDE.md, Observabilidade).
    reportNonFatal('admin_audit_billing_query_failed', { tag: TAG, ...serializeError(err) });
    throw err;
  }
}

/** Valida cada doc com `AuditBillingEventSchema`; inválido é descartado e reportado (#935). */
function parseRows(
  docs: QueryDocumentSnapshot[],
): AuditBillingEventRow[] {
  const out: AuditBillingEventRow[] = [];
  for (const d of docs) {
    const parsed = AuditBillingEventSchema.safeParse({ eventId: d.id, ...d.data() });
    if (parsed.success) {
      out.push(parsed.data);
    } else {
      // #935 (item 14): evento de audit que não parseia some da tela — o
      // report é o único jeito de saber que o log tem doc fora do schema.
      reportNonFatal('admin_audit_billing_parse_failed', {
        docId: d.id,
        issues: JSON.stringify(parsed.error.issues).slice(0, 1000),
      });
    }
  }
  return out;
}
